import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./TradeMarketSegment.css";
import InfoPopup from "../InfoPopup/InfoPopup";
import dropdownAngle from "../../../../assets/images/dropdownAngle.svg";
import Arrow from "../../../../assets/images/rightAnglePath.svg";

export const TRADE_MARKET_OPTIONS = [
  { key: "futures", label: "Futures" },
  { key: "spot", label: "Spot" },
  { key: "both", label: "Both" },
];

/**
 * Single-select dropdown: Futures | Spot | Both
 * Visual/UX control for which markets the exchange connection is for.
 */
const TradeMarketSegment = ({
  value = "both",
  onChange,
  className = "",
  label = "Trade markets",
  hint = "Choose which markets you’ll use with this connection.",
  recommendedValue = null,
  options = TRADE_MARKET_OPTIONS,
  ariaLabel = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  const selectedLabel =
    options.find((option) => option.key === value)?.label ||
    options[0]?.label ||
    "";

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      const target = event.target;
      const insideTrigger = dropdownRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  // Position the menu using fixed coordinates so it escapes any overflow:hidden
  // ancestors (e.g. the Connect Exchange modal-body / scroll wrapper) instead
  // of being clipped when it falls below the trigger.
  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 250;
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - menuWidth - 8)
    );
    setMenuPos({
      top: rect.bottom + 5,
      left,
      width: menuWidth,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return undefined;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectOption = (key) => {
    onChange?.(key);
    setIsOpen(false);
  };

  const renderMenu = (variant) => {
    const menuClass =
      variant === "fixed"
        ? "TradeMarketSegment__menu TradeMarketSegment__menu--fixed"
        : "TradeMarketSegment__menu";
    return (
      <ul
        className={menuClass}
        role="listbox"
        ref={variant === "fixed" ? menuRef : undefined}
        style={
          variant === "fixed" && menuPos
            ? {
                position: "fixed",
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                maxWidth: `calc(100vw - 16px)`,
              }
            : undefined
        }
      >
        {options.map((option) => {
          const isActive = value === option.key;
          const isRecommended = recommendedValue === option.key;
          return (
            <li key={option.key}>
              <button
                type="button"
                role="option"
                aria-selected={isActive}
                className={`TradeMarketSegment__option${
                  isActive ? " is-active" : ""
                }${isRecommended ? " is-recommended" : ""}`}
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  selectOption(option.key);
                }}
              >
                <span className="TradeMarketSegment__optionLabel">
                  {option.label}
                </span>
                {isRecommended ? (
                  <span className="TradeMarketSegment__badge">
                    Recommended
                  </span>
                ) : (
                  <span className="TradeMarketSegment__optionArrow">
                    <img alt="" src={Arrow} />
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const track = (
    <div
      className={`TradeMarketSegment__track${
        isOpen ? " is-open" : ""
      }`}
      ref={dropdownRef}
    >
      <button
        type="button"
        ref={triggerRef}
        className="TradeMarketSegment__trigger"
        aria-label={ariaLabel || label || "Select an option"}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="TradeMarketSegment__selectedValue">{selectedLabel}</span>
        <span className="TradeMarketSegment__arrow" aria-hidden="true">
          <img alt="" src={dropdownAngle} />
        </span>
      </button>

      {/* Original inline menu (preserved per code preservation rule).
          Kept for reference; the menu is now rendered as a fixed-position
          element below so it can escape the modal body's overflow:hidden. */}
      {/* {isOpen && (
        <ul className="TradeMarketSegment__menu" role="listbox">
          {options.map((option) => {
            const isActive = value === option.key;
            const isRecommended = recommendedValue === option.key;
            return (
              <li key={option.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`TradeMarketSegment__option${
                    isActive ? " is-active" : ""
                  }${isRecommended ? " is-recommended" : ""}`}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    selectOption(option.key);
                  }}
                >
                  <span className="TradeMarketSegment__optionLabel">
                    {option.label}
                  </span>
                  {isRecommended ? (
                    <span className="TradeMarketSegment__badge">
                      Recommended
                    </span>
                  ) : (
                    <span className="TradeMarketSegment__optionArrow">
                      <img alt="" src={Arrow} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )} */}
    </div>
  );

  if (!label && !hint) {
    return (
      <div className={`TradeMarketSegment${className ? ` ${className}` : ""}`}>
        {track}
        {isOpen && menuPos ? renderMenu("fixed") : null}
      </div>
    );
  }

  return (
    <div className={`TradeMarketSegment${className ? ` ${className}` : ""}`}>
      <div className="TradeMarketSegment__header">
        <div className="TradeMarketSegment__heading">
          {label ? <p className="title">{label}</p> : null}
          {hint ? (
            <InfoPopup
              wrapperClassName="TradeMarketSegment__info"
              contentItems={[{ type: "paragraph", text: hint }]}
            />
          ) : null}
        </div>
        {track}
      </div>
      {isOpen && menuPos ? renderMenu("fixed") : null}
    </div>
  );
};

export default TradeMarketSegment;
